'use client';

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import type { StaticImageData } from 'next/image';
import { useTRPC } from '@/lib/trpc/client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { resolveProductImageBySku } from '@/lib/product-images';

export type CartItem = {
  id: string;
  sku: string;
  name: string;
  price: number;
  quantity: number;
  image?: string | StaticImageData;
};

export type CartContextType = {
  cartId: string | null;
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
  isLoading: boolean;
  addItem: (product: Omit<CartItem, 'quantity'>) => Promise<string>;
  removeItem: (id: string) => void;
  updateQuantity: (
    id: string,
    quantity: number,
    cartIdOverride?: string,
  ) => void;
  resetCart: () => void;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
};

const CartContext = createContext<CartContextType | undefined>(undefined);

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

type CartProviderProps = {
  children: React.ReactNode;
};

const CART_ID_STORAGE_KEY = 'sapling-cart-id';

export function CartProvider({ children }: CartProviderProps) {
  const [cartId, setCartId] = useState<string | null>(null);
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const pendingCartCreation = useRef<Promise<string> | null>(null);

  const trpc = useTRPC();

  // tRPC mutations
  const createCartMutation = useMutation({
    ...trpc.cart.create.mutationOptions(),
  });
  const addItemMutation = useMutation({
    ...trpc.cart.addItem.mutationOptions(),
  });
  const updateQuantityMutation = useMutation({
    ...trpc.cart.updateQuantity.mutationOptions(),
  });
  const removeItemMutation = useMutation({
    ...trpc.cart.removeItem.mutationOptions(),
  });

  // tRPC query for cart data
  const cartQuery = useQuery({
    ...trpc.cart.get.queryOptions({ cartId: cartId! }),
    enabled: !!cartId,
    throwOnError: false,
    staleTime: 1000 * 60,
  });

  // Sync cart items to state when data changes
  useEffect(() => {
    if (cartQuery.data) {
      setItems(
        cartQuery.data.items.map((item) => ({
          id: item.sku,
          sku: item.sku,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image: resolveProductImageBySku(item.sku),
        })),
      );
    }
  }, [cartQuery.data]);

  // Clear stale cart ID on NOT_FOUND error
  useEffect(() => {
    const error = cartQuery.error;
    if (
      error &&
      typeof error === 'object' &&
      'data' in error &&
      error.data &&
      typeof error.data === 'object' &&
      'code' in error.data &&
      error.data.code === 'NOT_FOUND'
    ) {
      localStorage.removeItem(CART_ID_STORAGE_KEY);
      setCartId(null);
      setItems([]);
    }
  }, [cartQuery.error]);

  // Initialize cart on mount
  useEffect(() => {
    async function initializeCart() {
      const storedCartId = localStorage.getItem(CART_ID_STORAGE_KEY);

      if (storedCartId) {
        setCartId(storedCartId);
      }

      setIsInitialized(true);
    }

    initializeCart();
  }, []);

  // Create a new cart if needed
  const ensureCart = useCallback(async (): Promise<string> => {
    if (cartId) {
      return cartId;
    }

    // If cart creation is already in progress, wait for it
    if (pendingCartCreation.current) {
      return pendingCartCreation.current;
    }

    // Start cart creation and store the promise
    pendingCartCreation.current = createCartMutation
      .mutateAsync()
      .then((result) => {
        const newCartId = result.id;
        localStorage.setItem(CART_ID_STORAGE_KEY, newCartId);
        setCartId(newCartId);
        pendingCartCreation.current = null;
        return newCartId;
      })
      .catch((error) => {
        pendingCartCreation.current = null;
        throw error;
      });

    return pendingCartCreation.current;
  }, [cartId, createCartMutation]);

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );

  const addItem = useCallback(
    async (product: Omit<CartItem, 'quantity'>): Promise<string> => {
      const currentCartId = await ensureCart();

      const result = await addItemMutation.mutateAsync({
        cartId: currentCartId,
        sku: product.sku,
        quantity: 1,
      });

      setItems(
        result.items.map((item) => ({
          id: item.sku,
          sku: item.sku,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image: resolveProductImageBySku(item.sku),
        })),
      );

      return currentCartId;
    },
    [ensureCart, addItemMutation],
  );

  const removeItem = useCallback(
    async (id: string) => {
      if (!cartId) return;

      try {
        const result = await removeItemMutation.mutateAsync({
          cartId,
          sku: id,
        });

        setItems(
          result.items.map((item) => ({
            id: item.sku,
            sku: item.sku,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            image: resolveProductImageBySku(item.sku),
          })),
        );
      } catch (error) {
        console.error('Failed to remove item from cart:', error);
      }
    },
    [cartId, removeItemMutation],
  );

  const updateQuantity = useCallback(
    async (id: string, quantity: number, cartIdOverride?: string) => {
      const effectiveCartId = cartIdOverride || cartId;
      if (!effectiveCartId) return;

      try {
        const result = await updateQuantityMutation.mutateAsync({
          cartId: effectiveCartId,
          sku: id,
          quantity,
        });

        setItems(
          result.items.map((item) => ({
            id: item.sku,
            sku: item.sku,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            image: resolveProductImageBySku(item.sku),
          })),
        );
      } catch (error) {
        console.error('Failed to update item quantity:', error);
      }
    },
    [cartId, updateQuantityMutation],
  );

  const openCart = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeCart = useCallback(() => {
    setIsOpen(false);
  }, []);

  const resetCart = useCallback(() => {
    localStorage.removeItem(CART_ID_STORAGE_KEY);
    setCartId(null);
    setItems([]);
    setIsOpen(false);
  }, []);

  const isLoading =
    !isInitialized ||
    (cartId && cartQuery.isPending) ||
    createCartMutation.isPending ||
    addItemMutation.isPending ||
    updateQuantityMutation.isPending ||
    removeItemMutation.isPending;

  const contextValue: CartContextType = {
    cartId,
    items,
    totalItems,
    totalPrice,
    isLoading,
    addItem,
    removeItem,
    updateQuantity,
    resetCart,
    isOpen,
    openCart,
    closeCart,
  };

  if (!isInitialized) {
    return null;
  }

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
}
